import type { IWorkspace } from '@platform/core/types';
import { baseApi, run } from '@platform/state/api/baseApi';
import { authDao } from '../dao/authTransport';
import type { IAuthOptions } from '../types/IAuthOptions';
import type { IAuthSession } from '../types/IAuthSession';
import type { IAuthUser } from '../types/IAuthUser';
import type { ILoginRequest } from '../types/ILoginRequest';
import type { IMfaSetup } from '../types/IMfaSetup';
import type { IObserverPeople } from '../types/IObserverPeople';
import type { IObserverSession } from '../types/IObserverSession';
import type { IOnboardingWrite } from '../types/IOnboardingWrite';
import type { IRegisterRequest } from '../types/IRegisterRequest';
import type { SsoProvider } from '../types/SsoProvider';

/**
 * Вхід і режим спостерігача в RTK Query. Сесія (/auth/me) кешується під тегом «Me», як і профіль /me: зміну особи (вхід, вихід, початок і
 * кінець спостереження) сторінки завершують скиданням усього кешу API (resetApiState) — дані попередньої особи не лишаються.
 */
export const authApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    authOptions: b.query<IAuthOptions, void>({ queryFn: () => run(() => authDao.options()), keepUnusedDataFor: 300 }),
    authSession: b.query<IAuthSession | null, void>({ queryFn: () => run(() => authDao.session()), providesTags: ['Me'] }),
    authLogin: b.mutation<IAuthUser, ILoginRequest>({ queryFn: (body) => run(() => authDao.login(body)) }),
    authRegister: b.mutation<IAuthUser, IRegisterRequest>({ queryFn: (body) => run(() => authDao.register(body)) }),
    authMfaEnrol: b.mutation<IMfaSetup, void>({ queryFn: () => run(() => authDao.mfaEnrol()) }),
    authMfaEnrolConfirm: b.mutation<IAuthUser, string>({ queryFn: (code) => run(() => authDao.mfaEnrolConfirm(code)) }),
    authSso: b.mutation<IAuthUser, { provider: SsoProvider; demoEmail?: string | null }>({
      queryFn: ({ provider, demoEmail }) => run(() => authDao.sso(provider, demoEmail)),
    }),
    authLogout: b.mutation<void, void>({ queryFn: () => run(() => authDao.logout()) }),
    observerPeople: b.query<IObserverPeople, string>({ queryFn: (q) => run(() => authDao.people(q)), keepUnusedDataFor: 120 }),
    observerStart: b.mutation<IObserverSession, string>({ queryFn: (personId) => run(() => authDao.startObserver(personId)) }),
    observerEnd: b.mutation<void, void>({ queryFn: () => run(() => authDao.endObserver()) }),
    onboardWorkspace: b.mutation<IWorkspace, IOnboardingWrite>({ queryFn: (body) => run(() => authDao.onboard(body)), invalidatesTags: ['Me', 'Workspaces'] }),
  }),
});

export const {
  useAuthOptionsQuery, useAuthSessionQuery, useAuthLoginMutation, useAuthRegisterMutation, useAuthSsoMutation, useAuthLogoutMutation,
  useAuthMfaEnrolMutation, useAuthMfaEnrolConfirmMutation, useObserverPeopleQuery, useObserverStartMutation, useObserverEndMutation, useOnboardWorkspaceMutation,
} = authApi;
